/**
 * FilterBar — filters ON TOP of any list (boss rule), state in the URL through useTableState,
 * so every filtered list is a link. Multi-select chips reuse DataTable's FilterChip; the date
 * chip has presets and a custom range.
 *
 *   const t = useTableState(['status', 'client', 'date']);
 *   <FilterBar table={t} filters={[{ id: 'status', label: 'Status', options }]} total={q.data?.total} noun={['parcel', 'parcels']} />
 */
import { CalendarRange, ChevronDown, Plus, X } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { count } from '../../lib/format';
import { Button, cx } from './core';
import { FilterChip, type FilterDef } from './DataTable';
import { SearchInput } from './form';
import { Popover } from './popover';
import type { useTableState } from './table-state';

export type BarFilter = (FilterDef & { kind?: 'options' }) | { kind: 'date'; id: string; label: string };

export const DATE_PRESETS = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '12m', label: 'Last 12 months' },
  { value: 'year', label: 'This year' },
] as const;

/** "2026-01-01..2026-03-31" → { from, to }; presets → null. */
export function parseDateRange(v: string | undefined): { from: string; to: string } | null {
  const m = /^(\d{4}-\d{2}-\d{2})?\.\.(\d{4}-\d{2}-\d{2})?$/.exec(v ?? '');
  return m ? { from: m[1] ?? '', to: m[2] ?? '' } : null;
}

export function describeDate(v: string | undefined): string | null {
  if (!v) return null;
  const preset = DATE_PRESETS.find((p) => p.value === v);
  if (preset) return preset.label;
  const r = parseDateRange(v);
  if (!r) return v;
  if (r.from && r.to) return `${r.from} to ${r.to}`;
  return r.from ? `From ${r.from}` : `Until ${r.to}`;
}

function DateChip({ label, value, onChange }: { label: string; value: string | undefined; onChange: (v: string | undefined) => void }) {
  const range = parseDateRange(value);
  const [from, setFrom] = useState(range?.from ?? '');
  const [to, setTo] = useState(range?.to ?? '');
  useEffect(() => {
    setFrom(range?.from ?? '');
    setTo(range?.to ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const active = !!value;
  return (
    <div className="flex items-center">
      <Popover
        label={label}
        trigger={({ open, toggle, id }) => (
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={id}
            onClick={toggle}
            className={cx(
              'inline-flex min-h-touch items-center gap-1.5 rounded-full border px-3.5 text-sm font-medium transition-colors',
              active ? 'rounded-r-none border-r-0 border-brand bg-brand-soft pr-2 text-brand' : 'border-dashed border-strong text-muted hover:border-solid hover:text-ink',
            )}
          >
            {active ? <CalendarRange aria-hidden className="h-3.5 w-3.5" /> : <Plus aria-hidden className="h-3.5 w-3.5" />}
            {active ? `${label}: ${describeDate(value)}` : label}
            <ChevronDown aria-hidden className="h-3.5 w-3.5" />
          </button>
        )}
      >
        {(close) => (
          <div className="w-72 p-1">
            {DATE_PRESETS.map((p) => (
              <button
                key={p.value}
                type="button"
                role="menuitemradio"
                aria-checked={value === p.value}
                onClick={() => {
                  onChange(p.value);
                  close();
                }}
                className={cx('flex min-h-touch w-full items-center rounded-[4px] px-2.5 text-left text-sm hover:bg-sunken', value === p.value && 'font-semibold text-brand')}
              >
                {p.label}
              </button>
            ))}
            <form
              className="mt-1 grid grid-cols-2 gap-2 border-t border-line p-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (from || to) onChange(`${from}..${to}`);
                close();
              }}
            >
              <label className="text-xs font-semibold">
                From
                <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className="mt-1 block min-h-10 w-full rounded-[4px] border border-strong bg-card px-2 text-sm" />
              </label>
              <label className="text-xs font-semibold">
                To
                <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className="mt-1 block min-h-10 w-full rounded-[4px] border border-strong bg-card px-2 text-sm" />
              </label>
              <Button type="submit" size="sm" className="col-span-2" disabled={!from && !to}>
                Use these dates
              </Button>
            </form>
          </div>
        )}
      </Popover>
      {active && (
        <button type="button" aria-label={`Remove ${label} filter`} onClick={() => onChange(undefined)} className="grid min-h-touch w-9 place-items-center rounded-r-full border border-l-0 border-brand bg-brand-soft text-brand hover:bg-brand/15">
          <X aria-hidden className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

export interface SortOption {
  value: string;
  label: string;
}

export function FilterBar({ table, filters, search, sorts, total, noun = ['result', 'results'], right, className }: { table: ReturnType<typeof useTableState>; filters: BarFilter[]; /** Show a text box filtering within the list (not the global search). */ search?: { placeholder: string }; sorts?: SortOption[]; total?: number; /** Singular / plural for the count line. */ noun?: [string, string]; right?: ReactNode; className?: string }) {
  const { state, set } = table;
  const [q, setQ] = useState(state.q);
  useEffect(() => setQ(state.q), [state.q]);
  useEffect(() => {
    if (q === state.q) return;
    const t = setTimeout(() => set({ q }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  const setFilter = (id: string, v: string[]) => set({ filters: { ...state.filters, [id]: v } });
  // Without its own text box the list's q belongs to someone else (the search page): keep it.
  const anyFilter = (!!search && !!state.q) || Object.values(state.filters).some((v) => v.length);
  const sortValue = state.sort ? `${state.sort.dir === 'desc' ? '-' : ''}${state.sort.id}` : sorts?.[0]?.value ?? '';
  return (
    <div className={cx('flex flex-col gap-2 border-b border-line p-3', className)}>
      <div className="flex flex-wrap items-center gap-2">
        {search && <SearchInput label={search.placeholder} placeholder={search.placeholder} value={q} onChange={setQ} className="min-w-[200px] flex-1 sm:max-w-xs" />}
        {filters.map((f) =>
          f.kind === 'date' ? (
            <DateChip key={f.id} label={f.label} value={state.filters[f.id]?.[0]} onChange={(v) => setFilter(f.id, v ? [v] : [])} />
          ) : f.options.length ? (
            <FilterChip key={f.id} def={f} values={state.filters[f.id] ?? []} onChange={(v) => setFilter(f.id, v)} />
          ) : null,
        )}
        <div className="ml-auto flex items-center gap-2">
          {sorts && sorts.length > 1 && (
            <label className="flex items-center gap-2 text-sm text-muted">
              <span className="hidden sm:inline">Sort</span>
              <span className="sr-only sm:hidden">Sort</span>
              <select
                value={sortValue}
                onChange={(e) => {
                  const v = e.target.value;
                  set({ sort: { id: v.replace(/^-/, ''), dir: v.startsWith('-') ? 'desc' : 'asc' } }, false);
                }}
                className="min-h-touch rounded-tag border border-strong bg-card px-2 text-sm text-ink"
              >
                {sorts.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          {right}
        </div>
      </div>
      {anyFilter && (
        <div className="flex flex-wrap items-center gap-2 text-sm" aria-live="polite">
          {total !== undefined && (
            <span className="text-muted">
              {count(total)} {total === 1 ? noun[0] : noun[1]}
            </span>
          )}
          <button type="button" onClick={() => set({ q: search ? '' : state.q, filters: Object.fromEntries(Object.keys(state.filters).map((k) => [k, []])) })} className="min-h-9 font-semibold text-brand underline-offset-2 hover:underline">
            Clear filters
          </button>
        </div>
      )}
    </div>
  );
}

/** Previous / next paging under a list, with "21–40 of 312". State in the URL like the filters. */
export function Pager({ table, total, className }: { table: ReturnType<typeof useTableState>; total: number | undefined; className?: string }) {
  const { state, set } = table;
  if (!total || total <= state.pageSize) return null;
  const pages = Math.ceil(total / state.pageSize);
  const from = (state.page - 1) * state.pageSize + 1;
  const to = Math.min(total, state.page * state.pageSize);
  return (
    <nav aria-label="Pages" className={cx('flex items-center justify-between gap-3 border-t border-line px-3 py-2 text-sm', className)}>
      <span className="tabular text-muted">
        {count(from)}–{count(to)} of {count(total)}
      </span>
      <span className="flex gap-1">
        <Button variant="secondary" size="sm" className="min-h-touch" disabled={state.page <= 1} onClick={() => set({ page: state.page - 1 }, false)}>
          Previous
        </Button>
        <Button variant="secondary" size="sm" className="min-h-touch" disabled={state.page >= pages} onClick={() => set({ page: state.page + 1 }, false)}>
          Next
        </Button>
      </span>
    </nav>
  );
}
